import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { agentConfigured, loadStudio, siteUrl, startAgentBuild, type AgentAllowance, type AgentImage, type AgentSite } from '../../api/agent';
import { ApiError } from '../../api/client';
import { Screen } from '../../ui';
import Composer from './Composer';
import './studio.css';

const EXAMPLES = [
  'A Flappy Bird–style game with a little finch, sound and a high score',
  'A habit tracker with streaks and a really satisfying check-off',
  'A birthday party invite site with RSVP and a countdown',
  'A drum machine I can play with my thumbs',
];

/**
 * /studio — the agent builder. Say what you want (and show it with a screenshot); a Claude agent
 * builds it, checks it on a phone-sized screen, and publishes it at its own address.
 */
export default function Studio() {
  const navigate = useNavigate();
  const [sites, setSites] = useState<AgentSite[] | null>(null);
  /** 'plan': open to Monthly subscribers, and this account is not one (2026-11-03). */
  const [closed, setClosed] = useState<false | 'plan' | 'closed'>(false);
  const [allowance, setAllowance] = useState<AgentAllowance | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!agentConfigured()) return;
    let live = true;
    loadStudio().then(
      (s) => {
        if (!live) return;
        setSites(s.sites);
        setAllowance(s.allowance);
      },
      (e) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 403) setClosed(e.code === 'agent_builder_plan' ? 'plan' : 'closed');
        setSites([]);
      },
    );
    return () => {
      live = false;
    };
  }, []);

  async function build(prompt: string, images: AgentImage[]) {
    setBusy(true);
    setProblem(null);
    try {
      const { buildId } = await startAgentBuild(prompt, images);
      navigate(`/studio/builds/${buildId}`);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not start. Try again in a moment.');
      setBusy(false);
    }
  }

  if (!agentConfigured()) {
    return (
      <Screen title="Studio">
        <p className="studio-muted">The studio isn't set up in this build of Saddle.</p>
      </Screen>
    );
  }

  if (closed === 'plan') {
    return (
      <Screen title="Studio">
        <p className="studio-lede">
          The studio builds games, tools and sites with a Claude agent that tests them on a phone before you see them. It comes with the Monthly plan: 5 new sites a month.
        </p>
        <button type="button" className="studio-example pressable" onClick={() => navigate('/credits')}>See the Monthly plan</button>
      </Screen>
    );
  }

  if (closed) {
    return (
      <Screen title="Studio">
        <p className="studio-muted">The new builder isn't open to your account yet. It's coming soon.</p>
      </Screen>
    );
  }

  return (
    <Screen title="What should we make?">
      <p className="studio-lede">
        Describe it, or add a screenshot of something you'd like copied. We'll build it, test it on a phone, and give you a link to share.
      </p>
      <Composer
        placeholder="A game, an app, a tool, a website…"
        submitLabel="Build it"
        busy={busy}
        onSubmit={build}
        examples={EXAMPLES}
      />
      {problem && <p className="studio-problem" role="alert">{problem}</p>}
      {allowance && !allowance.unlimited && (
        <p className="studio-muted" data-testid="studio-allowance">
          {allowance.sitesLeft > 0
            ? `${allowance.sitesLeft} of ${allowance.sitesPerMonth} new sites left this month.`
            : `You've made this month's ${allowance.sitesPerMonth} new sites. More on ${new Date(`${allowance.resetsOn}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' })}.`}
        </p>
      )}

      {sites && sites.length > 0 && (
        <section className="studio-section" aria-labelledby="studio-sites">
          <h2 id="studio-sites" className="studio-h2">Your sites</h2>
          <ul className="studio-sites">
            {sites.map((s) => (
              <li key={s.siteId}>
                <button type="button" className="studio-site-row pressable" onClick={() => navigate(`/studio/sites/${s.siteId}`)}>
                  <span className="studio-site-initial" aria-hidden="true">{(s.title ?? '?').slice(0, 1).toUpperCase()}</span>
                  <span className="studio-site-text">
                    <span className="studio-site-title">{s.title ?? 'Building…'}</span>
                    <span className="studio-site-url">{s.slug ? siteUrl(s.slug).replace('https://', '') : 'Not live yet'}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Screen>
  );
}
